import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Volume2, VolumeX, ArrowLeft, ArrowRight, FileText, HandHeart,
  CalendarClock, DollarSign, AlertTriangle, HelpCircle, ListChecks, Info,
  MessageCircle, Send, Loader2, Mic, MicOff, ZoomIn, ZoomOut, X, Maximize2,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getLetter, getOwnership, letterImageUrl, askQuestion } from "@/lib/api";
import { speak, stop, ttsSupported } from "@/lib/tts";
import { Textarea } from "@/components/ui/textarea";

const URGENCY_STYLES = {
  high: { label: "High urgency", cls: "bg-red-50 border-red-700 text-red-800", dot: "bg-red-700" },
  medium: { label: "Some urgency", cls: "bg-amber-50 border-amber-700 text-amber-800", dot: "bg-amber-600" },
  low: { label: "Low urgency", cls: "bg-emerald-50 border-emerald-700 text-emerald-800", dot: "bg-emerald-700" },
  unclear: { label: "Urgency unclear", cls: "bg-slate-100 border-slate-500 text-slate-700", dot: "bg-slate-500" },
};

function formatDeadline(analysis) {
  if (analysis.deadline_text) return analysis.deadline_text;
  if (analysis.deadline) {
    try {
      const d = new Date(analysis.deadline + "T00:00:00");
      return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch { return analysis.deadline; }
  }
  return "No deadline found";
}

export default function Explanation() {
  const { id } = useParams();
  const nav = useNavigate();
  const [letter, setLetter] = useState(null);
  const [err, setErr] = useState(null);
  const [reading, setReading] = useState(false);
  const [imgOpen, setImgOpen] = useState(false);

  // Q&A state
  const [qaHistory, setQaHistory] = useState([]);
  const [qaInput, setQaInput] = useState("");
  const [qaSending, setQaSending] = useState(false);
  const [listening, setListening] = useState(false);
  const qaScrollRef = useRef(null);
  const recognitionRef = useRef(null);
  // Track whether the current Q&A cycle was kicked off by voice — used to auto-speak the answer back.
  const voiceModeRef = useRef(false);

  const toggleVoiceInput = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't available in this browser. Try Chrome or Safari on your phone.");
      return;
    }
    if (listening) {
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      setListening(false);
      return;
    }
    // User tapped the mic — remember so we speak the assistant's answer back later.
    voiceModeRef.current = true;
    // Stop any currently playing TTS so the mic can hear the senior clearly.
    stop();
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;  // keep listening until the senior taps Stop
    let finalTranscript = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalTranscript += chunk + " ";
        else interim += chunk;
      }
      setQaInput((finalTranscript + interim).trim());
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed") {
        toast.error("Please allow microphone access to use voice input.");
      } else if (e.error === "no-speech") {
        toast("We didn't catch that — tap Speak and try again.", { icon: "🎤" });
      }
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    toast("Listening — take your time and speak clearly. Tap Stop when done.", { duration: 4000, icon: "🎤" });
    try { rec.start(); } catch { setListening(false); }
  };

  const ownership = getOwnership(id);
  const ownerToken = ownership?.ownerToken;

  useEffect(() => {
    if (!ownerToken) { setErr("This letter isn't available on this device."); return; }
    let alive = true;
    getLetter(id, ownerToken)
      .then((d) => {
        if (!alive) return;
        setLetter(d);
        setQaHistory(d?.qa_history || []);
      })
      .catch(() => setErr("We couldn't load this letter. It may have expired."));
    return () => { alive = false; stop(); };
  }, [id, ownerToken]);

  useEffect(() => {
    // Auto-scroll chat to bottom when new messages arrive
    if (qaScrollRef.current) {
      qaScrollRef.current.scrollTop = qaScrollRef.current.scrollHeight;
    }
  }, [qaHistory]);

  const doAskQuestion = async () => {
    const q = qaInput.trim();
    if (!q) return;
    // Capture voice-mode flag at submit time so a slow response doesn't miss the auto-speak
    const shouldSpeak = voiceModeRef.current;
    voiceModeRef.current = false;
    setQaSending(true);
    // Optimistic
    const optimisticNow = new Date().toISOString();
    setQaHistory((h) => [...h, { role: "user", content: q, at: optimisticNow }]);
    setQaInput("");
    try {
      const r = await askQuestion(id, ownerToken, q);
      setQaHistory(r.history || []);
      // Voice-in → voice-out: if the senior asked with the mic, speak the answer back.
      if (shouldSpeak) {
        const lastAssistant = [...(r.history || [])].reverse().find((m) => m.role === "assistant");
        if (lastAssistant?.content) {
          setTimeout(() => speak(lastAssistant.content, { rate: 0.95 }), 350);
        }
      }
    } catch (e) {
      // Never leave the senior empty-handed — surface the letter's own contact info as a fallback
      const phoneMatch = (letter?.analysis?.extracted_text || "").match(/1?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/);
      const fallback =
        "I'm having trouble answering right now — please try again in a moment." +
        (phoneMatch ? `\n\nFor immediate help, you can call the number on the letter: ${phoneMatch[0]}` : "") +
        (letter?.analysis?.sender_name ? `\n\nSender: ${letter.analysis.sender_name}` : "");
      setQaHistory((h) => [...h, { role: "assistant", content: fallback, at: new Date().toISOString() }]);
      if (shouldSpeak) setTimeout(() => speak(fallback, { rate: 0.95 }), 350);
      toast.error("Couldn't reach the assistant — showing the letter's phone number instead.");
    } finally {
      setQaSending(false);
    }
  };

  const analysis = letter?.analysis;
  const urgency = URGENCY_STYLES[analysis?.urgency] || URGENCY_STYLES.unclear;

  const readAloudText = useMemo(() => {
    if (!analysis) return "";
    const dl = analysis.deadline ? `Important date: ${formatDeadline(analysis)}. ` : "No deadline was found in this letter. ";
    const amt = analysis.amount ? `Amount: ${analysis.amount}. ` : "";
    return [
      analysis.plain_language_summary,
      "\n",
      `What you need to do: ${analysis.required_action || "No specific action was found."}`,
      "\n",
      dl,
      amt,
    ].filter(Boolean).join(" ");
  }, [analysis]);

  const toggleRead = () => {
    if (reading) { stop(); setReading(false); return; }
    if (!ttsSupported()) { toast.error("Read Aloud isn't available in this browser."); return; }
    speak(readAloudText, {
      onStart: () => setReading(true),
      onEnd: () => setReading(false),
      onError: () => { setReading(false); toast.error("Read Aloud stopped."); },
    });
  };

  if (err) {
    return (
      <>
        <Header />
        <main className="mx-auto max-w-3xl px-6 py-16 text-center" data-testid="explanation-error">
          <h1 className="font-display text-3xl font-bold text-slate-900">We couldn't open that letter</h1>
          <p className="mt-4 text-lg text-slate-700">{err}</p>
          <Link to="/upload" className="mt-8 inline-flex items-center gap-2 rounded-full bg-moss-700 px-6 py-3 text-lg font-bold text-bone">
            Upload a letter <ArrowRight strokeWidth={2.5} className="h-5 w-5" />
          </Link>
        </main>
      </>
    );
  }

  if (!letter) {
    return (
      <>
        <Header />
        <main className="mx-auto max-w-3xl px-6 py-24 text-center">
          <p className="text-xl text-slate-600">Reading your letter…</p>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main data-testid="explanation-page" className="mx-auto max-w-6xl px-6 py-10">
        <button onClick={() => nav("/upload")} className="mb-4 inline-flex items-center gap-2 text-base font-semibold text-moss-700 hover:underline" data-testid="explanation-back">
          <ArrowLeft strokeWidth={2.5} className="h-5 w-5" /> Back
        </button>

        {/* Header row */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-moss-700">Here's what this letter says</p>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold leading-tight text-slate-900" data-testid="letter-doc-type">
              {analysis.document_type || "A letter you received"}
            </h1>
            {analysis.sender_name && (
              <p className="mt-2 text-lg text-slate-600" data-testid="letter-sender">From: {analysis.sender_name}</p>
            )}
          </div>
          <button
            onClick={toggleRead}
            className={`inline-flex items-center gap-3 rounded-full px-7 py-4 text-lg font-bold min-h-[56px] ${
              reading ? "bg-red-700 text-bone hover:bg-red-800 cn-pulse" : "bg-moss-700 text-bone hover:bg-moss-800"
            }`}
            data-testid="read-aloud-btn"
          >
            {reading ? (
              <><VolumeX strokeWidth={2.5} className="h-6 w-6" /> Stop reading</>
            ) : (
              <><Volume2 strokeWidth={2.5} className="h-6 w-6" /> Read Aloud</>
            )}
          </button>
        </div>

        {/* AI uncertainty warning — shown when confidence is low OR uncertainties exist */}
        {(analysis.confidence === "low" || analysis.deadline_confidence === "low" || analysis.amount_confidence === "low" || (analysis.uncertainties && analysis.uncertainties.length > 0)) && (
          <div
            className="mt-6 rounded-3xl border-2 border-amber-500 bg-amber-50 p-5 sm:p-6 flex items-start gap-4"
            data-testid="ai-uncertainty-warning"
          >
            <AlertTriangle strokeWidth={2.5} className="mt-1 h-7 w-7 text-amber-800 flex-shrink-0" />
            <div>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-amber-900">
                Please double-check this with a person you trust
              </h3>
              <p className="mt-2 text-lg leading-relaxed text-amber-900">
                The AI wasn't fully confident reading this letter. Before you rely on the date, amount, or action below, we recommend having a family member, a friend, or someone at the sender's office confirm the details.
              </p>
              {analysis.uncertainties?.length > 0 && (
                <ul className="mt-3 space-y-1 text-base text-amber-900">
                  {analysis.uncertainties.map((u, i) => (
                    <li key={i} className="flex gap-2"><span>•</span><span>{u}</span></li>
                  ))}
                </ul>
              )}
              {analysis.deadline_confidence === "low" && (
                <p className="mt-3 text-base font-semibold text-amber-900">
                  ⚠️ The deadline date is uncertain. You'll need to confirm it before we can send a reminder.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Urgency bar */}
        <div className={`mt-6 inline-flex items-center gap-3 rounded-full border-2 px-5 py-2.5 text-base font-semibold ${urgency.cls}`} data-testid="letter-urgency">
          <span className={`h-3 w-3 rounded-full ${urgency.dot}`} />
          {urgency.label}
        </div>

        {/* Two columns: image + cards */}
        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          {/* Letter image — big enough for a senior to double-check the AI reading */}
          <aside className="cn-fade-up">
            <div className="rounded-3xl border-2 border-slate-200 bg-white p-3 shadow-sm">
              <p className="px-2 pt-1 pb-2 text-sm font-semibold uppercase tracking-wider text-moss-700">
                Your letter — tap image to zoom in
              </p>
              <button
                onClick={() => setImgOpen(true)}
                className="block w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 hover:ring-4 hover:ring-moss-700/20 transition"
                data-testid="letter-image-thumb"
                aria-label="Open the letter image in a zoomable viewer"
              >
                <img
                  src={letterImageUrl(id, ownerToken)}
                  alt="The letter you uploaded"
                  className="w-full h-auto object-contain"
                />
              </button>
              <div className="mt-3 flex items-start justify-between gap-3 px-2">
                <p className="flex items-center gap-2 text-sm text-slate-500">
                  <Info strokeWidth={2.5} className="h-4 w-4 flex-shrink-0" />
                  Please double-check the AI&apos;s reading against the letter above.
                </p>
                <button
                  onClick={() => setImgOpen(true)}
                  data-testid="open-zoom-viewer-btn"
                  className="inline-flex items-center gap-1.5 rounded-full border-2 border-moss-700 bg-white px-3 py-1.5 text-xs font-bold text-moss-700 hover:bg-moss-50 flex-shrink-0"
                >
                  <Maximize2 strokeWidth={2.5} className="h-3.5 w-3.5" />
                  Zoom in
                </button>
              </div>
            </div>
          </aside>

          {/* Explanation cards */}
          <section className="cn-fade-up space-y-5" data-testid="explanation-cards">
            <ExplainCard
              icon={FileText}
              title="What this is about"
              testId="card-about"
              body={analysis.plain_language_summary || "We couldn't produce a clear summary for this letter."}
            />
            <ExplainCard
              icon={HandHeart}
              title="What you need to do"
              testId="card-action"
              body={analysis.required_action || "No specific action was found in this letter."}
            />
            <ExplainCard
              icon={CalendarClock}
              title="Important date"
              testId="card-date"
              body={
                analysis.deadline
                  ? formatDeadline(analysis)
                  : "No deadline found — but here's what this letter says."
              }
              subtle={!analysis.deadline}
            />
            {analysis.amount && (
              <ExplainCard
                icon={DollarSign}
                title="Amount"
                testId="card-amount"
                body={analysis.amount}
              />
            )}
            <ExplainCard
              icon={AlertTriangle}
              title="How urgent"
              testId="card-urgency"
              body={
                analysis.urgency === "high" ? "This one asks for a fairly quick response." :
                analysis.urgency === "medium" ? "There's some urgency, but you have time to think." :
                analysis.urgency === "low" ? "This isn't urgent. Take your time." :
                "We couldn't tell how urgent this is."
              }
            />

            {analysis.important_points?.length > 0 && (
              <div className="rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid="card-important-points">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-moss-700 text-bone">
                    <ListChecks strokeWidth={2.5} className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-2xl font-bold text-slate-900">Key points</h3>
                </div>
                <ul className="mt-4 space-y-2.5 text-lg leading-relaxed text-slate-800">
                  {analysis.important_points.map((p, i) => (
                    <li key={i} className="flex gap-3"><span className="text-moss-700">•</span><span>{p}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.uncertainties?.length > 0 && (
              <div className="rounded-3xl border-2 border-amber-300 bg-amber-50 p-6" data-testid="card-uncertainties">
                <div className="flex items-center gap-3">
                  <HelpCircle strokeWidth={2.5} className="h-5 w-5 text-amber-800" />
                  <h4 className="font-display text-xl font-bold text-amber-900">Things we weren't sure about</h4>
                </div>
                <ul className="mt-3 space-y-2 text-lg text-amber-900">
                  {analysis.uncertainties.map((u, i) => <li key={i}>· {u}</li>)}
                </ul>
              </div>
            )}
          </section>
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 rounded-3xl border-2 border-slate-200 bg-white p-8 sm:p-10 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Ready for the next step?</h2>
            <p className="mt-1 text-lg text-slate-700">Save the deadline and, if you'd like, loop in someone you trust.</p>
          </div>
          <button
            onClick={() => nav(`/letter/${id}/action`)}
            className="inline-flex items-center gap-3 rounded-full bg-moss-700 px-8 py-4 text-lg font-bold text-bone hover:bg-moss-800 min-h-[56px]"
            data-testid="create-action-card-btn"
          >
            Create Action Card <ArrowRight strokeWidth={2.5} className="h-5 w-5" />
          </button>
        </div>

        {/* Q&A companion */}
        <section
          className="mt-10 rounded-3xl border-2 border-ember-600/30 bg-gradient-to-br from-ember-50/60 via-white to-white p-6 sm:p-8"
          data-testid="qa-companion"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-ember-700 text-bone">
              <MessageCircle strokeWidth={2.5} className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900">Ask about this letter</h2>
              <p className="text-base text-slate-600">Confused about something? Ask a question in your own words.</p>
            </div>
          </div>

          {qaHistory.length > 0 && (
            <div
              ref={qaScrollRef}
              className="mt-5 max-h-[380px] overflow-y-auto space-y-4 rounded-2xl bg-white border-2 border-slate-200 p-4"
              data-testid="qa-history"
            >
              {qaHistory.map((turn, i) => (
                <div key={i} className={`flex ${turn.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-lg leading-relaxed whitespace-pre-wrap ${
                      turn.role === "user"
                        ? "bg-moss-700 text-bone rounded-br-md"
                        : "bg-slate-100 text-slate-900 rounded-bl-md"
                    }`}
                    data-testid={turn.role === "user" ? "qa-user-msg" : "qa-assistant-msg"}
                  >
                    {turn.content}
                  </div>
                </div>
              ))}
              {qaSending && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 text-slate-600 rounded-2xl rounded-bl-md px-4 py-3 text-lg flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" /> Thinking…
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Textarea
                value={qaInput}
                onChange={(e) => setQaInput(e.target.value)}
                rows={2}
                maxLength={800}
                placeholder="e.g. What happens if I miss this deadline?"
                className="w-full text-lg border-2 border-slate-300 rounded-2xl p-4 bg-white"
                data-testid="qa-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    doAskQuestion();
                  }
                }}
              />
              <p className="mt-1 text-xs text-slate-500">
                Tip: press Ctrl/Cmd + Enter to send · {qaInput.length}/800
              </p>
            </div>
            <button
              onClick={toggleVoiceInput}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-4 text-lg font-bold min-h-[56px] ${
                listening
                  ? "bg-red-700 text-bone hover:bg-red-800 cn-pulse"
                  : "border-2 border-ember-700 bg-white text-ember-700 hover:bg-ember-50"
              }`}
              data-testid="qa-voice-btn"
              aria-label={listening ? "Stop listening" : "Speak your question"}
            >
              {listening ? (
                <><MicOff strokeWidth={2.5} className="h-5 w-5" /> Stop</>
              ) : (
                <><Mic strokeWidth={2.5} className="h-5 w-5" /> Speak</>
              )}
            </button>
            <button
              onClick={doAskQuestion}
              disabled={qaSending || !qaInput.trim()}
              className="inline-flex items-center gap-2 rounded-full bg-ember-700 px-6 py-4 text-lg font-bold text-bone hover:bg-ember-600 disabled:opacity-60 min-h-[56px]"
              data-testid="qa-send-btn"
            >
              <Send strokeWidth={2.5} className="h-5 w-5" />
              {qaSending ? "Sending…" : "Ask"}
            </button>
          </div>

          {qaHistory.length === 0 && !qaSending && (
            <div className="mt-5 grid gap-2 sm:grid-cols-2" data-testid="qa-example-questions">
              {[
                "What happens if I miss this deadline?",
                "Do I need to bring anything?",
                "Who do I call if I have questions?",
                "What if I'm not sure this is right for me?",
              ].map((ex) => (
                <button
                  key={ex}
                  onClick={() => setQaInput(ex)}
                  className="text-left rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 text-base text-slate-700 hover:border-ember-600 hover:bg-ember-50/50"
                  data-testid="qa-suggested"
                >
                  {ex}
                </button>
              ))}
            </div>
          )}

          <p className="mt-4 text-xs text-slate-500">
            The AI can be wrong. For legal, medical, or financial decisions, always talk to a person you trust.
          </p>
        </section>
      </main>

      {/* Zoomable image viewer — real zoom + pan for legibility */}
      {imgOpen && (
        <ZoomableImageViewer
          src={letterImageUrl(id, ownerToken)}
          onClose={() => setImgOpen(false)}
        />
      )}
      <Footer />
    </>
  );
}

function ZoomableImageViewer({ src, onClose }) {
  // Zoom is expressed as a percentage: 100 = fit-container natural size.
  // Actual step: 25%. Range: 50% .. 400%. Native scroll handles panning when zoomed.
  const [zoom, setZoom] = useState(150);
  const scrollRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(400, z + 25));
      if (e.key === "-") setZoom((z) => Math.max(50, z - 25));
      if (e.key === "0") setZoom(150);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Re-center the scroll on the middle of the image whenever zoom changes so
  // seniors don't lose their place when they tap the + / – buttons.
  const onZoomChange = (nextZoom) => {
    const el = scrollRef.current;
    if (!el) { setZoom(nextZoom); return; }
    const centerX = (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth;
    const centerY = (el.scrollTop + el.clientHeight / 2) / el.scrollHeight;
    setZoom(nextZoom);
    requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      const sw = scrollRef.current.scrollWidth;
      const sh = scrollRef.current.scrollHeight;
      scrollRef.current.scrollLeft = centerX * sw - scrollRef.current.clientWidth / 2;
      scrollRef.current.scrollTop = centerY * sh - scrollRef.current.clientHeight / 2;
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-slate-900"
      data-testid="image-lightbox"
      role="dialog"
      aria-label="Letter image zoom viewer"
    >
      {/* Top toolbar */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-800 bg-slate-950/80 px-4 py-3">
        <div className="flex items-center gap-2 text-bone">
          <FileText strokeWidth={2.5} className="h-5 w-5 text-amber-300" />
          <span className="text-base font-semibold">Your letter</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onZoomChange(Math.max(50, zoom - 25))}
            disabled={zoom <= 50}
            data-testid="zoom-out-btn"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-800 text-bone hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Zoom out"
          >
            <ZoomOut strokeWidth={2.5} className="h-5 w-5" />
          </button>
          <span
            className="min-w-[68px] rounded-full bg-slate-800 px-3 py-2 text-center text-sm font-bold text-bone"
            data-testid="zoom-percent-label"
          >
            {zoom}%
          </span>
          <button
            type="button"
            onClick={() => onZoomChange(Math.min(400, zoom + 25))}
            disabled={zoom >= 400}
            data-testid="zoom-in-btn"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-800 text-bone hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Zoom in"
          >
            <ZoomIn strokeWidth={2.5} className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => onZoomChange(150)}
            data-testid="zoom-reset-btn"
            className="ml-2 rounded-full bg-slate-800 px-4 py-2 text-sm font-bold text-bone hover:bg-slate-700"
            aria-label="Reset zoom"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="close-lightbox-btn"
            className="ml-2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-800 text-bone hover:bg-rose-700"
            aria-label="Close viewer"
          >
            <X strokeWidth={2.5} className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Scrollable image container. Image width grows with zoom, native scroll pans. */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto"
        style={{ touchAction: "pinch-zoom" }}
      >
        <div className="flex justify-center p-6">
          <img
            src={src}
            alt="Letter (zoomable)"
            className="rounded-xl border-2 border-white/20 shadow-2xl"
            style={{ width: `${zoom}%`, maxWidth: "none", height: "auto" }}
            draggable={false}
          />
        </div>
      </div>

      {/* Hint bar */}
      <div className="border-t border-slate-800 bg-slate-950/80 px-4 py-2 text-center text-xs text-slate-400">
        Use <b className="text-slate-200">+</b> / <b className="text-slate-200">–</b> to zoom · <b className="text-slate-200">Esc</b> to close · Scroll to pan
      </div>
    </div>
  );
}

function ExplainCard({ icon: Icon, title, body, testId, subtle = false }) {
  return (
    <article className={`cn-card-lift rounded-3xl border-2 bg-white p-6 sm:p-8 ${subtle ? "border-slate-200" : "border-slate-200"}`} data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-moss-700 text-bone">
          <Icon strokeWidth={2.5} className="h-5 w-5" />
        </span>
        <h3 className="font-display text-2xl font-bold text-slate-900">{title}</h3>
      </div>
      <p className={`mt-4 text-xl leading-relaxed ${subtle ? "text-slate-500 italic" : "text-slate-800"}`}>
        {body}
      </p>
    </article>
  );
}
